import { useState } from "react";
import { Smartphone, CheckCircle2, AlertTriangle, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MODELS, modelConfig, badModels, clearBadModels, markBadModel } from "@/lib/models";
import { transcribeFile } from "@/lib/pipeline";
import { resetWorker } from "@/lib/worker";
import { burnVideo } from "@/lib/burn";
import { PRESETS } from "@/lib/presets";
import { hasWordTiming } from "@/lib/lines";

async function deviceInfo(device) {
  const est = navigator.storage?.estimate ? await navigator.storage.estimate().catch(() => null) : null;
  return {
    userAgent: navigator.userAgent,
    deviceMemoryGB: device?.memory, cpuCores: device?.cores, webgpu: device?.webgpu, shaderF16: device?.f16,
    jsHeapLimitMB: performance.memory ? Math.round(performance.memory.jsHeapSizeLimit / 1048576) : "n/a",
    storageQuotaMB: est ? Math.round(est.quota / 1048576) : "n/a",
    screen: `${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}x`,
    previousCrashes: badModels(),
  };
}

const sampleUrl = () => {
  const mp4 = document.createElement("video").canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"');
  return `${process.env.PUBLIC_URL || ""}/test-media/hindi_test.${mp4 ? "mp4" : "webm"}`;
};

const Step = ({ label, s }) => (
  <li className="flex items-start gap-2 text-sm">
    {s?.state === "run" ? <Loader2 className="h-4 w-4 mt-0.5 animate-spin text-[var(--cc-accent)]" /> : s?.state === "ok" ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-400" /> : s?.state === "fail" ? <AlertTriangle className="h-4 w-4 mt-0.5 text-rose-400" /> : <span className="h-4 w-4 mt-0.5 rounded-full border border-[var(--cc-border-strong)]" />}
    <span><b>{label}</b>{s?.text && <span className="block text-xs text-[var(--cc-text-2)] break-words">{s.text}</span>}</span>
  </li>
);

export const PhoneTest = ({ open, onOpenChange, device }) => {
  const [model, setModel] = useState(device?.recommended || "small");
  const [doExport, setDoExport] = useState(true);
  const [steps, setSteps] = useState({});
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const set = (k, v) => setSteps((s) => ({ ...s, [k]: v }));

  const run = async () => {
    setRunning(true);
    setReport(null);
    setSteps({});
    const rep = { app: "CaptionCraft AI phone test", date: new Date().toISOString() };
    set("info", { state: "run" });
    rep.device = await deviceInfo(device);
    set("info", { state: "ok", text: `WebGPU ${device?.webgpu ? "yes" : "no"} · ~${device?.memory || "?"} GB RAM · heap limit ${rep.device.jsHeapLimitMB} MB` });
    const cfg = modelConfig(model, device, false);
    let segs = null;
    let sample = null;
    set("asr", { state: "run", text: `${cfg.label} on ${cfg.modeLabel} (~${cfg.sizeMB} MB first time)…` });
    try {
      const blob = await (await fetch(sampleUrl())).blob();
      sample = new File([blob], sampleUrl().split("/").pop(), { type: blob.type || "video/webm" });
      const file = sample;
      rep.sample = file.name;
      const out = await transcribeFile({
        file, cfg, language: "hi", wordList: "", wordTiming: true, lines: "off", cache: {}, isCancelled: () => false,
        onState: (st) => set("asr", { state: "run", text: st.detail }),
      });
      segs = out.segs;
      rep.transcription = { ok: true, ...out.stats, wordTimingCaptions: segs.filter(hasWordTiming).length, text: segs.map((c) => c.text).join(" | ") };
      set("asr", { state: "ok", text: `Load ${(out.stats.loadMs / 1000).toFixed(1)}s · transcribe ${(out.stats.transcribeMs / 1000).toFixed(1)}s (speed ${out.stats.rtf}× real-time) · word timing ${rep.transcription.wordTimingCaptions}/${segs.length}` });
    } catch (e) {
      rep.transcription = { ok: false, model: cfg.label, device: cfg.device, code: e.code, error: e.message };
      if (["OOM", "WORKER", "GPU"].includes(e.code)) markBadModel(cfg.cacheKey);
      set("asr", { state: "fail", text: `${e.code || "ERROR"}: ${e.message}` });
    }
    resetWorker("Phone test finished");
    if (doExport && segs) {
      set("exp", { state: "run", text: "Rendering 10 s sample in real time…" });
      const t = performance.now();
      try {
        const file = sample;
        const r = await burnVideo({ file, captions: segs, style: PRESETS[1].style, mobile: device?.mobile, signal: new AbortController().signal, onProgress: (p) => set("exp", { state: "run", text: `Rendering… ${Math.round(p * 100)}%` }) });
        rep.export = { ok: true, ms: Math.round(performance.now() - t), mime: r.mime, sizeKB: Math.round(r.blob.size / 1024), size: `${r.width}x${r.height}` };
        set("exp", { state: "ok", text: `${r.mime} ${r.width}×${r.height}, ${rep.export.sizeKB} KB in ${(rep.export.ms / 1000).toFixed(1)}s — verified playable` });
      } catch (e) {
        rep.export = { ok: false, code: e.code, error: e.message };
        set("exp", { state: "fail", text: e.message });
      }
    }
    const rtf = rep.transcription?.rtf;
    rep.verdict = !rep.transcription?.ok
      ? `${cfg.label} failed here — the app will now recommend a smaller model on this device.`
      : `${cfg.label} works. A 1-minute video should take about ${Math.ceil((rtf * 60) / 6) * 6 || 6}s to transcribe; 3 minutes ≈ ${Math.ceil(rtf * 3)} min.`;
    setReport(rep);
    setRunning(false);
  };

  const text = report ? JSON.stringify(report, null, 2) : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Report copied — paste it in the chat"); }
    catch (e) { toast.error("Copy blocked — select the text below and copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !running && onOpenChange(o)}>
      <DialogContent className="bg-[var(--cc-panel)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="phone-test-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-[var(--cc-accent)]" /> Phone test</DialogTitle>
          <DialogDescription className="text-[var(--cc-text-2)]">Runs a real transcription of a 10-second Hindi sample and an optional video export on this device, then gives you a report to share. Nothing is uploaded.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm font-semibold" htmlFor="pt-model">Model to test</label>
          <select id="pt-model" value={model} onChange={(e) => setModel(e.target.value)} disabled={running} data-testid="phone-test-model"
            className="w-full h-11 rounded-md bg-[var(--cc-elev)] border border-[var(--cc-border-strong)] px-3 text-sm">
            {Object.keys(MODELS).map((k) => <option key={k} value={k} disabled={k === "turbo" && !device?.webgpu}>{`${MODELS[k].label} — ~${modelConfig(k, device, false).sizeMB} MB${k === "turbo" && !device?.webgpu ? " (needs WebGPU)" : ""}`}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={doExport} onChange={(e) => setDoExport(e.target.checked)} disabled={running} data-testid="phone-test-export-toggle" className="h-4 w-4" /> Also test burned-in video export</label>
          <Button onClick={run} disabled={running} className="w-full h-11 font-bold" data-testid="phone-test-run-btn">{running ? "Testing… keep this screen on" : "Start phone test"}</Button>
          <ol className="space-y-2" data-testid="phone-test-steps">
            <Step label="Device info" s={steps.info} />
            <Step label="Transcription (Hindi sample, word timing on)" s={steps.asr} />
            {doExport && <Step label="Video export" s={steps.exp} />}
          </ol>
          {report && (
            <div className="space-y-2" data-testid="phone-test-report">
              <p className="text-sm font-semibold text-[var(--cc-accent)]" data-testid="phone-test-verdict">{report.verdict}</p>
              <Button variant="secondary" className="w-full" onClick={copy} data-testid="phone-test-copy-btn"><Copy className="h-4 w-4 mr-1" /> Copy report</Button>
              <textarea readOnly value={text} rows={6} className="w-full text-[11px] font-mono-ts rounded-md bg-[var(--cc-bg)] border border-[var(--cc-border-strong)] p-2" data-testid="phone-test-report-text" />
            </div>
          )}
          {badModels().length > 0 && !running && (
            <button className="text-xs underline text-[var(--cc-text-2)]" onClick={() => { clearBadModels(); toast.success("Crash memory cleared — reload to re-check"); }} data-testid="phone-test-clear-crashes">Forget previous crash records</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
